import { getNode, capturePropertyState, applyPropertyState, wireConfigToPropertyState } from '../nodeUtils.js';
import { matchesObject } from '../matchesObject.js';

/**
 * Reconstruct an object's full state by replaying all PropertyChangeCommands
 * that affect it, up to and including the entry at targetIndex.
 * Returns null if the object's creation config cannot be found.
 */
function reconstructObjectState(entries, targetIndex, objectUuid) {
    let state = null;

    for (let i = 0; i <= targetIndex; i++) {
        const entry = entries[i];
        if (!matchesObject(entry, objectUuid)) continue;

        // ObjectCreateCommand carries the initial config — a flat, wire-keyed
        // JSONL-shaped object (what layer.importObject() expects), not the
        // {properties, styles} shape capturePropertyState produces. Normalize
        // it so every subsequent accumulation step operates on one shape.
        if (entry.config && entry.objectUuid === objectUuid) {
            state = wireConfigToPropertyState(entry.config);
        }

        // PropertyChangeCommand carries full before/after state
        if (entry.nextState && entry.objectUuid === objectUuid) {
            state = { ...entry.nextState };
        }

        // ObjectMoveCommand: update x/y in accumulated state
        if (entry.finalX !== undefined && entry.objectUuid === objectUuid) {
            if (state) {
                state.x = entry.finalX;
                state.y = entry.finalY;
            }
        }

        // ObjectResizeCommand: update x/y/w/h
        if (entry.finalW !== undefined && entry.objectUuid === objectUuid) {
            if (state) {
                state.x = entry.finalX;
                state.y = entry.finalY;
                state.w = entry.finalW;
                state.h = entry.finalH;
            }
        }
    }

    return state;
}

export default class RestoreObjectVersionCommand {
    constructor({ objectUuid, restoredState, currentState, pageUuid }) {
        this.id = crypto.randomUUID();
        this.timestamp = Date.now();
        this.description = 'Restore Version';
        this.category = 'structure';
        this.objectUuid = objectUuid;
        this.affectedUuids = [objectUuid];
        this.pageUuid = pageUuid ?? null;
        this.restoredState = restoredState;
        this.currentState  = currentState;
    }

    execute() {
        const node = getNode(this.objectUuid);
        if (node) applyPropertyState(node, this.restoredState);
    }

    undo() {
        const node = getNode(this.objectUuid);
        if (node) applyPropertyState(node, this.currentState);
    }

    toJSON() {
        return {
            type: 'RestoreObjectVersion',
            id: this.id,
            timestamp: this.timestamp,
            payload: { objectUuid: this.objectUuid, pageUuid: this.pageUuid },
        };
    }
}

export { reconstructObjectState };
